import { useIsAuthenticated, useMsal } from "@azure/msal-react";

import { signIn, signOut } from "@/lib/auth/msal";

/**
 * Top-right user menu. Rendered in `AppShell`.
 *
 * Signed out -> "Sign in" button.
 * Signed in  -> initials chip with a small dropdown (sign out).
 *
 * Kept dependency-light deliberately: a `<details>` element gives us a
 * native dropdown without pulling in @radix-ui/react-dropdown-menu for
 * a single use. Switch to the radix component when we add a second
 * action (e.g. theme toggle in 3.6d).
 */
export function UserMenu() {
  const { accounts } = useMsal();
  const isAuthed = useIsAuthenticated();

  if (!isAuthed) {
    return (
      <button
        type="button"
        onClick={() => {
          void signIn();
        }}
        className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
      >
        Sign in
      </button>
    );
  }

  const account = accounts[0];
  const name = account?.name ?? account?.username ?? "Signed in";
  const initials = name
    .split(/\s+/)
    .map((p) => p.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <details className="relative">
      <summary className="list-none cursor-pointer select-none flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-secondary/60 transition-colors">
        <span
          className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 font-mono text-xs font-semibold text-primary"
          aria-hidden="true"
        >
          {initials}
        </span>
        <span className="text-sm">{name}</span>
      </summary>
      <div className="absolute right-0 mt-2 w-48 rounded-md border border-border bg-popover p-1 shadow-md z-10">
        <button
          type="button"
          onClick={() => {
            void signOut();
          }}
          className="block w-full rounded-sm px-3 py-2 text-left text-sm hover:bg-secondary/60 transition-colors"
        >
          Sign out
        </button>
      </div>
    </details>
  );
}
